import { useEffect, useState } from 'react';
import { gql, useMutation, useQuery } from '@apollo/client';
import { Button, Input } from '@artstorefronts/ui';
import AdminLayout from '../../components/AdminLayout';

const GET_DM_TRANSLATION_SETTINGS = gql`
  query dmTranslationSettings {
    dmTranslationSettings {
      dailyUsdCap
      dailyCallCap
    }
  }
`;

const UPDATE_DM_TRANSLATION_SETTINGS = gql`
  mutation updateDmTranslationSettings($input: UpdateDmTranslationSettingsInput!) {
    updateDmTranslationSettings(input: $input) {
      dailyUsdCap
      dailyCallCap
    }
  }
`;

interface DmTranslationSettings {
  dailyUsdCap: number;
  dailyCallCap: number;
}

function DmTranslationPage() {
  const { data, loading } = useQuery<{ dmTranslationSettings: DmTranslationSettings }>(GET_DM_TRANSLATION_SETTINGS, {
    fetchPolicy: 'cache-and-network',
  });
  const [updateSettings, { loading: saving }] = useMutation(UPDATE_DM_TRANSLATION_SETTINGS);

  const [usd, setUsd] = useState('100');
  const [calls, setCalls] = useState('150000');
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    const settings = data?.dmTranslationSettings;
    if (!settings) return;
    setUsd(String(settings.dailyUsdCap));
    setCalls(String(settings.dailyCallCap));
  }, [data?.dmTranslationSettings]);

  const handleSave = async () => {
    setError(null);
    setSaved(false);
    const parsedUsd = Number(usd);
    if (usd.trim() === '' || !Number.isFinite(parsedUsd) || parsedUsd < 0) {
      setError('Daily spend cap must be a number of 0 or more.');
      return;
    }
    const parsedCalls = Number(calls);
    if (calls.trim() === '' || !Number.isInteger(parsedCalls) || parsedCalls < 0) {
      setError('Daily call cap must be a whole number of 0 or more.');
      return;
    }
    try {
      await updateSettings({
        variables: { input: { dailyUsdCap: parsedUsd, dailyCallCap: parsedCalls } },
      });
      setSaved(true);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Could not save.';
      setError(message);
    }
  };

  return (
    <AdminLayout
      title="DM translation"
      description="Daily caps for translating direct messages. Saving stores an admin override. Until then, the server uses DM_TRANSLATION_DAILY_USD_CAP or $100, and DM_TRANSLATION_DAILY_CALL_CAP or 150,000."
    >
      {loading && !data ? (
        <div>Loading…</div>
      ) : (
        <div className="max-w-2xl space-y-8">
          <section>
            <h2 className="text-lg font-semibold mb-2">Daily spend cap (USD)</h2>
            <p className="text-sm text-gray-500 mb-2">
              Estimated model spend per America/Chicago day. 0 stops translation as soon as the budget check runs.
            </p>
            <Input
              type="number"
              min={0}
              step={0.01}
              value={usd}
              onChange={(e) => setUsd(e.target.value)}
              className="w-40"
            />
          </section>

          <section>
            <h2 className="text-lg font-semibold mb-2">Daily call cap</h2>
            <p className="text-sm text-gray-500 mb-2">
              How many translation calls are allowed on the same calendar day.
            </p>
            <Input
              type="number"
              min={0}
              step={1}
              value={calls}
              onChange={(e) => setCalls(e.target.value)}
              className="w-40"
            />
          </section>

          <div className="flex items-center gap-3">
            <Button type="button" onClick={handleSave} disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </Button>
            {saved ? <span className="text-sm text-green-600">Saved.</span> : null}
            {error ? <span className="text-sm text-red-600">{error}</span> : null}
          </div>
        </div>
      )}
    </AdminLayout>
  );
}

export default DmTranslationPage;
